/**
 * Sentry Integration with PII Scrubbing
 * 
 * Error monitoring configured for GDPR compliance.
 * Automatically removes/masks sensitive data before sending to Sentry.
 */

import * as Sentry from '@sentry/react';
import { redactSensitiveData } from './privacy';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development';

// PII patterns to scrub
const PII_PATTERNS = {
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  phone: /(\+45\s?)?\d{8}/g,
  cpr: /\b\d{6}-?\d{4}\b/g,
  token: /\b[A-Za-z0-9-_]{20,}\.[A-Za-z0-9-_]{20,}\.[A-Za-z0-9-_]{20,}\b/g, // JWT pattern
};

/**
 * Scrubs PII from strings
 */
function scrubString(str: string): string {
  let scrubbed = str;
  
  scrubbed = scrubbed.replace(PII_PATTERNS.email, '[EMAIL_REDACTED]');
  scrubbed = scrubbed.replace(PII_PATTERNS.phone, '[PHONE_REDACTED]');
  scrubbed = scrubbed.replace(PII_PATTERNS.cpr, '[CPR_REDACTED]');
  scrubbed = scrubbed.replace(PII_PATTERNS.token, '[TOKEN_REDACTED]');
  
  return scrubbed;
}

/**
 * Initialize Sentry with GDPR-compliant configuration
 */
export function initSentry() {
  // Only initialize if DSN is provided (production/staging)
  if (!SENTRY_DSN) {
    console.info('Sentry DSN not configured - error monitoring disabled');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    
    // Performance monitoring
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
    
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',
    
    // Trace propagation targets (moved from BrowserTracingOptions per Sentry v10 API)
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/[^/]*\.supabase\.co/,
    ],

    // Integration configuration
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session replay for debugging (with privacy masking)
        maskAllText: true, // Mask all text by default
        maskAllInputs: true, // Mask all inputs
        blockAllMedia: true, // Don't capture media
      }),
    ],
    
    // Session replay sampling
    replaysSessionSampleRate: 0, // Disable by default (enable per user if needed)
    replaysOnErrorSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    
    // PII scrubbing - GDPR compliance
    beforeSend(event, hint) {
      // Scrub user data
      if (event.user) {
        event.user = {
          id: event.user.id, // Keep ID for tracking (use hash if needed)
          // Remove email, username, ip_address
        };
      }
      
      // Scrub breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.message) {
            breadcrumb.message = scrubString(breadcrumb.message);
          }
          if (breadcrumb.data) {
            breadcrumb.data = redactSensitiveData(breadcrumb.data);
          }
          return breadcrumb;
        });
      }
      
      // Scrub exception messages
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map(exception => {
          if (exception.value) {
            exception.value = scrubString(exception.value);
          }
          return exception;
        });
      }
      
      // Scrub request data
      if (event.request) {
        // Remove query string if it contains sensitive data
        if (event.request.url) {
          const url = new URL(event.request.url);
          // Remove tokens from URL
          url.searchParams.delete('token');
          url.searchParams.delete('access_token');
          url.searchParams.delete('refresh_token');
          event.request.url = url.toString();
        }
        
        // Remove headers that might contain PII
        if (event.request.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
          delete event.request.headers['X-API-Key'];
        }
        
        // Scrub POST data
        if (event.request.data) {
          event.request.data = redactSensitiveData(event.request.data);
        }
      }
      
      // Scrub extra context
      if (event.extra) {
        event.extra = redactSensitiveData(event.extra);
      }
      
      // Scrub tags
      if (event.tags) {
        const sensitiveTagKeys = ['email', 'phone', 'user_email', 'username'];
        sensitiveTagKeys.forEach(key => {
          if (event.tags && event.tags[key]) {
            delete event.tags[key];
          }
        });
      }
      
      return event;
    },
    
    // Don't send errors in development by default
    enabled: ENVIRONMENT !== 'development',
    
    // Ignore known benign errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      // Random errors
      'Non-Error promise rejection captured',
    ],
    
    // Filter out localhost and extension URLs
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });
  
  console.info('✅ Sentry initialized with GDPR-compliant PII scrubbing');
}

/**
 * Set user context (use with caution - no PII!)
 */
export function setSentryUser(userId: string, role?: string) {
  Sentry.setUser({
    id: userId, // Only non-PII identifier
    role: role,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb (auto-scrubbed)
 */
export function addBreadcrumb(
  message: string,
  category: string = 'user-action',
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, any>
) {
  Sentry.addBreadcrumb({
    message: scrubString(message),
    category,
    level,
    data: data ? redactSensitiveData(data) : undefined,
  });
}

/**
 * Capture exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context ? redactSensitiveData(context) : undefined,
  });
}

/**
 * Capture message manually
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, any>
) {
  Sentry.captureMessage(scrubString(message), {
    level,
    extra: context ? redactSensitiveData(context) : undefined,
  });
}

/**
 * Set context tags (no PII!)
 */
export function setContext(name: string, context: Record<string, any>) {
  Sentry.setContext(name, redactSensitiveData(context));
}

// Export Sentry for ErrorBoundary usage
export { Sentry };







